import { useNavigate, useParams } from "react-router-dom";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  connectFunctionsEmulator,
  getFunctions,
} from "firebase/functions";
import {
  Typography,
  Card,
  CardHeader,
  CardBody,
  IconButton,
  Carousel,
} from "@material-tailwind/react";
import {
  ArrowUpIcon,
  ChartBarIcon,
  TicketIcon,
  CreditCardIcon,
  ClockIcon,
  InformationCircleIcon
} from "@heroicons/react/24/outline";
import { StatisticsCard } from "widgets/cards/statistics-card";
import { collection, doc, getDoc, orderBy, query } from "firebase/firestore";
import db from "../../firebase";
import { ArrowDownIcon, ArrowLeftCircleIcon, CalendarDaysIcon, PhoneIcon } from "@heroicons/react/24/solid";
import { useCollection, useDocument } from "react-firebase-hooks/firestore";
import { formatFirebaseTimestamp, getUniquePriceHistoryChanges } from "utils";
import { formatPrice, friendlyModelName, friendlyTimeFormat, locationFromListingScrapeData } from "../../utils";
import { ListingDetailImage } from "../../components/ListingDetailImage";
import { ListingDetailsChart } from "../../components/ListingDetailsChart";
import PriceHistoryDialog from "./PriceHistoryDialog";
import { DataContext } from "../../context/DataContext";
import { ControllerStatisticsChart } from "../../components/ControllerStatisticsChart";

const options = {
  snapshotListenOptions: { includeMetadataChanges: true },
};
const col = "listing_aggregations_v1";

const functions = getFunctions();
  
if (window.location.href.indexOf('localhost') > -1) {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001)
}

function filterUrlsForMostRecent(urls){

  const cached = {};

  for(let i = 0; i< urls.length;i++){
    const link = urls[i].link;
    const dt = urls[i].dt;

    const spl = link.split('ppp')[1].split('?')[0];
    const name = `ppp_${spl}`;

    if (cached[name]){
      if (dt > cached[name].dt){
        cached[name] = urls[i];
      }
    } else {
      cached[name] = urls[i];
    }
  }
  const ret = Object.values(cached);
  // console.log(ret[0].dt.toDate())
  return ret;


}

const ListingDetails = ({ulid}) => {
  const { slug } = useParams();
  const uniqueListingId = slug ? slug : ulid;
  const queryListing = query(doc(db, `${col}/${uniqueListingId}`));
  const [listingData] = useDocument(queryListing, options);
  const dataContext = useContext(DataContext);

  const queryListingScrapes = useMemo(()=> query(
    collection(db, `${col}/${uniqueListingId}/listing_scrapes`),
    orderBy("scrapeTs", "asc"),
  ), [uniqueListingId]);
  
  const [listingScrapesData] = useCollection(queryListingScrapes, options);

  function timeseriesFromData(data) {
    let priceUSDOfLastPriceChange = data[0].data().price_usd;
    const points = data.map((item, index) => {
      const dt = item.data().scrapeTs.toDate();
      if (index !== 0){
        if (item.data().currency === data[index-1].data().currency){
          // use the last price_usd unless the underlying price has changed
          const price = item.data().price;
          const prevPrice = data[index-1].data().price;
          if (price !== prevPrice){
            priceUSDOfLastPriceChange = item.data().price_usd;
            // console.log(`price diff ${dt} ${price} ${prevPrice}`)
            return {dt, price: item.data().price_usd};
          } else {
            return {dt, price: priceUSDOfLastPriceChange};
          }
        } else {
          priceUSDOfLastPriceChange = item.data().price_usd;
          return {dt, price: item.data().price_usd};
        }
      } else {
        return {dt, price: item.data().price_usd};
      }
    });
    return points;
  }

  function allNaN(timeseries){
    return timeseries.filter(item => isNaN(item.price)).length === timeseries.length;
  }

  const navigate = useNavigate()

  const handleBack = ()=>{
    navigate(-1);
  }

  const lastListingScrape = () => {
    if (!listingScrapesData) return
    const latestListingScrapeData = listingScrapesData.docs[listingScrapesData.docs.length-1].data();
    return latestListingScrapeData;
  }

  const dealerInfo = () => {

    const ret = {
      dealerName: 'Loading...',
      dealerPhoneNumber: "Loading...",
      dealerLocation: "Loading..."
    }

    if (!listingData){
      return ret;
    }

    ret.dealerName = listingData.data().dealerName;
    ret.dealerPhoneNumber = listingData.data().dealerPhoneNumber
    ret.individualName = listingData.data().individualName;
    ret.dealerLocation = listingData.data().dealerLocation;

    
    if (ret.dealerName){
      ret.dealerName = ret.dealerName.replaceAll(`&amp;`,``);
      ret.dealerName = ret.dealerName.replaceAll(` ,`,`,`);
    }
    if (ret.individualName){
      ret.individualName = ret.individualName.replaceAll(`&amp;`,``);
      ret.individualName = ret.individualName.replaceAll(` ,`,`,`);
    }
    if (ret.dealerLocation){
      ret.dealerLocation = ret.dealerLocation.replaceAll(`&amp;`,``);
      ret.dealerLocation = ret.dealerLocation.replaceAll(` ,`,`,`);
    }
    return ret;
  }

  const [urls, setUrls] = useState();

  useEffect(() => {
    const path = `listing_aggregations_v1/${uniqueListingId}/listing_images/img`;
    const q = query(doc(db, path));
    
    getDoc(q).then((doc) => {
      if (doc.data()){
        // console.log(doc.data().urls);
        // filter urls to only use latest stored image name
        // check logic for storing images trade-a-plane, what happens when we detect a new images. fs
        const filteredForRecent = filterUrlsForMostRecent(doc.data().urls)
        setUrls(filteredForRecent);      
      } else{
        console.log(`doc does not exist`);
      }        
    });
  },[uniqueListingId]);

  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);

  return (
    <>

      <PriceHistoryDialog open={priceHistoryOpen} setOpen={setPriceHistoryOpen} listingScrapesData={listingScrapesData}></PriceHistoryDialog>

      <div className="mb-0 p-0">

          <IconButton
            variant="text"
            onClick={handleBack}
            className="border-solid border-2 border-lma-dark"
          >
            <ArrowLeftCircleIcon className="h-7 w-7 text-lma-dark" />
          </IconButton>

      </div>

      <div className="mb-12 grid gap-y-5 gap-x-3 md:grid-cols-2 xl:grid-cols-2">

        <div className="col-span-1 h-full">

          {listingData && 
            <ListingDetailImage
            src={listingData.data().imageURL}
            price={
              formatPrice(listingData.data().price, listingData.data().currency)
            }
            title={listingData.data().title}
            reg={listingData.data().reg}
            site={listingData.data().source}
            listingURL={listingData.data().listingURL}
            lastSeen={listingData.data().last_seen}
            location={locationFromListingScrapeData(lastListingScrape())}
            country={listingData.data().country}
            year={listingData.data().year}
            model={friendlyModelName(dataContext, listingData.data().model)}
            uniqueListingId={listingData.data().uniqueListingId}
            bPath={lastListingScrape()?.bPath}
          />}
        </div>      
        {listingData && urls &&

        <div className="col-span-1">
            <Card className="h-full">
              <Carousel loop={true} autoplay={false} className="">
              {urls && urls.length && urls.map((m)=>{
                return <img
                src={m.link}
                key={m.link}
                alt=""
                className="h-full max-h-96 w-full object-cover"
              />
              })}
              </Carousel>
            </Card>
        </div> }
      </div>
      
      <div className="mb-12 grid gap-y-8 gap-x-3 md:grid-cols-5 xl:grid-cols-5">

      {listingData && listingData.data().timeState && (
        <StatisticsCard
          key={"timing"}
          value={
            listingData.data().timeState.totalTime
          }
          title={"Total Time"}
          icon={React.createElement(ClockIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <>  
              {/* <Typography className="font-normal text-sm text-lma-gray">
                Number of Engines: {listingData.data().timeState.numberOfEngines}
              </Typography> */}
              {(listingData.data().timeState.numberOfEngines === 1) &&
                <>
                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine Time: {listingData.data().timeState.engine1Time}
                  </Typography>
                </>}
              {(listingData.data().timeState.numberOfEngines === 2) &&
                <>
                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine 1: {listingData.data().timeState.engine1Time}
                  </Typography>

                  <Typography className="font-normal text-sm text-lma-gray">
                    Engine 2: {listingData.data().timeState.engine2Time}
                  </Typography>
                </>}
            </>
         }
        />)}


      {listingData && (
        <StatisticsCard
          key={"dealer"}
          value={
            dealerInfo().dealerPhoneNumber
          }
          title={"Dealer"}
          icon={React.createElement(PhoneIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <>  
              <Typography className="font-normal text-sm text-lma-gray">
              {dealerInfo().dealerName}
              </Typography>
              <Typography className="font-normal text-sm text-lma-gray">
                Contact: {dealerInfo().individualName}
              </Typography>
              <Typography className="font-normal text-sm text-lma-gray">
                Location: {dealerInfo().dealerLocation}
              </Typography>
            </>
         }
        />)}

      {listingData && listingScrapesData  &&(
        <StatisticsCard
          key={"price"}
          //   rest={"jello"}
          value={
            listingData ? formatPrice(listingData.data().price, listingData.data().currency) : "loading..."
          }
          title={"Current Price"}
          icon={React.createElement(CreditCardIcon, {
            className: "w-6 h-6 text-white",
          })}
          footer={
            <div>
            <Typography className="font-normal text-sm text-lma-gray">
              {"Orignal Price "}
              <strong className={"text-blue-gray"}>
              {listingData ? formatPrice(listingScrapesData.docs[0].data().price, listingScrapesData.docs[0].data().currency) : "loading..."}</strong>
            </Typography>
            <Typography className="font-normal text-sm text-lma-gray">
              {"active for "}
              <strong className={"text-blue-gray"}>
                {Math.ceil(listingData.data().days_active)}</strong>&nbsp;{ Math.ceil(listingData.data().days_active) === 1 ? "day" : "days"}
            </Typography>
            </div>
          }
        />)}

      {listingData && (<StatisticsCard
          key={"price_change"}
          //   rest={"jello"}
          value={
            listingData ? formatPrice(listingData.data().price_change_usd, "USD") : "loading..."
          }
          title={"Price Change vs. original"}
          icon={React.createElement(ChartBarIcon, {
            className: "w-6 h-6 text-red",
          })}
          footer={
            <Typography className="font-normal text-sm text-lma-gray">
              <strong
                className={
                  listingData && listingData.data().price_change_pct_usd >= 0
                    ? "text-green-500"
                    : "text-red-500"
                }
              >
                {Math.abs(listingData.data().price_change_pct_usd) > 0 ? Math.ceil(listingData.data().price_change_pct_usd * 100) + "%" : ""}
              </strong>
              &nbsp;
              {Math.abs(listingData.data().price_change_pct_usd) > 0  ? (listingData.data().price_change_pct_usd >= 0 ? "increase" : "decrease"): ""}
              {Math.abs(listingData.data().price_change_pct_usd) === 0  ? "No change" : ""}
            </Typography>
          }
        />)}

        {listingData && (
          <StatisticsCard

            key={"price_changes"}
            value={
              friendlyTimeFormat(listingData.data().last_change_dt)
            }
            title={"Since last price change"}
            icon={React.createElement(CalendarDaysIcon, {
              className: "w-6 h-6 text-white",
            })}
            footer={
              <Typography className="font-normal text-sm text-lma-gray">
                <strong>
                  {listingData.data().no_price_changes}
                </strong>
                &nbsp;
                {(listingData.data().no_price_changes > 1) || (listingData.data().no_price_changes === 0)
                  ? "price changes"
                  : "price change"}
              </Typography>
            }
          />
        )}
      </div>

      {listingData && <div className="mb-4 grid grid-cols-1 gap-6 xl:grid-cols-10">
       
      <div className="xl:col-span-3 col-span-1">
        <Card className="h-full">
          <CardHeader
            floated={false}
            shadow={false}
            color="transparent"
            className="m-0 p-6"
          >
            <Typography variant="h6" className="text-lma-dark mb-2">
              Price Changes
            </Typography>

            <IconButton
              size="sm"
              variant="text"
              onClick={(e) => setPriceHistoryOpen(true)}
              className="!absolute top-5 right-5 w-5 h-5 "
            >
              <InformationCircleIcon className="w-5 h-5" />
            </IconButton> 

          </CardHeader>
          {listingScrapesData && 
          <CardBody>
           {
              getUniquePriceHistoryChanges(listingScrapesData.docs).map(
                ({ price_usd, currency, scrapeTs, change }, key) => (
                  <div key={key} className="flex items-start gap-4 py-3">
                    <div
                      className={`relative p-1 after:absolute after:-bottom-6 after:left-2/4 after:w-0.5 after:-translate-x-2/4 after:bg-gray-200 after:content-[''] ${
                        key === listingScrapesData.docs.length - 1
                          ? "after:h-0"
                          : "after:h-4/6"
                      }`}
                    >
                    
                      {change === 1 && (
                        <ArrowUpIcon
                          strokeWidth={3}
                          className="h-5 w-5 text-green-500"
                        />
                      )}
                      {change === -1 && (
                        <ArrowDownIcon
                          strokeWidth={5}
                          className="h-5 w-5 text-red-500"
                        />
                      )}
                      {change === 0 && (
                        <TicketIcon
                          strokeWidth={5}
                          className="h-5 w-5 text-green-500"
                        />
                      )}
                    </div>
                    <div>
                      <Typography
                        className="text-lma-dark block font-medium text-lg"
                      >
                        {formatPrice(price_usd, currency)}
                      </Typography>
                      <Typography
                        as="span"

                        className="text-md font-medium text-lma-gray"
                      >
                        {formatFirebaseTimestamp(scrapeTs)}
                      </Typography>
                    </div>
                  </div>
                )
                      )}
          </CardBody>}
        </Card>
        </div>

      <div className="col-span-1 h-full xl:col-span-7">
              <Card className="h-full">
              <CardHeader
            floated={false}
            shadow={false}
            color="transparent"
            className="m-0 flex items-center justify-between p-6"
          >
            <div>
              <Typography variant="h6" className="text-lma-dark mb-1">
                Chart
              </Typography>
            </div>
          </CardHeader>
              <CardBody>
            {listingScrapesData && (!allNaN(timeseriesFromData(listingScrapesData.docs)) ? (
                <ListingDetailsChart
                  timeseries={timeseriesFromData(listingScrapesData.docs)}
                  priceChange={listingData.data().price_change_usd}
                  price={listingData.data().price}
                ></ListingDetailsChart>
              ) :   <Typography
              variant="small"
              className="text-[11px] font-medium uppercase text-lma-gray"
            >
              Data unavailable
            </Typography>)}
              </CardBody>
          </Card>
            
        </div>

        {listingData.data().source === `controller` && listingData.data().dealerName === `Lone Mountain Aircraft` 
          && <div className="col-span-1 h-full xl:col-span-12">
            {listingScrapesData ? 
                <ControllerStatisticsChart listingId={listingData?.data().listingId}></ControllerStatisticsChart>
               :   <Typography
              variant="small"
              className="text-[11px] font-medium uppercase text-lma-gray"
            >
              Data unavailable
            </Typography>}
            
            
        </div>}
        
      </div>}

    </>
  );
};

export default ListingDetails;
